import { useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Type for the URL signer function that generates signed upload URLs
 */
export type UrlSignerFunction = (
  extension: string,
  fileType: string,
  fileName: string,
  fileSize: number,
) => Promise<{ fullPath: string; signedUrl: string }>;

/**
 * Options for file validation
 */
export interface FileValidationOptions {
  maxSize?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
}

/**
 * Options for upload behavior
 */
export interface UploadOptions {
  noSuccessToast?: boolean;
}

/**
 * Map of file extensions to MIME types for files with missing type detection
 */
const extensionToMimeMap: Record<string, string> = {
  'pdf': 'application/pdf',
  'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'md': 'text/markdown',
  'markdown': 'text/markdown',
  'csv': 'text/csv',
  'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'png': 'image/png',
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'gif': 'image/gif',
  'webp': 'image/webp',
};

/**
 * Derives the MIME type for a file, using extension fallback if needed
 */
function deriveFileType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream') {
    return file.type;
  }
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return extensionToMimeMap[ext] || 'application/octet-stream';
}

/**
 * Custom hook that encapsulates file upload logic.
 * Provides reusable validation and upload functions for any file type.
 *
 * @param urlSigner - Function to generate signed URLs for uploads
 * @param validationOptions - File validation configuration
 * @param uploadOptions - Upload behavior configuration
 * @returns Object with validateFile, uploadFile, and uploadMultipleFiles functions
 */
export function useFileUpload(
  urlSigner: UrlSignerFunction,
  validationOptions: FileValidationOptions = {},
  uploadOptions: UploadOptions = {}
) {
  const { maxSize, allowedTypes, allowedExtensions } = validationOptions;
  const { noSuccessToast } = uploadOptions;

  /**
   * Validates a file against configured rules (size, type, extension).
   * Shows toast error messages for validation failures.
   *
   * @param file - File to validate
   * @returns true if file is valid, false otherwise
   */
  const validateFile = useCallback(
    (file: File): boolean => {
      if (!file) {
        toast.error('File is not specified');
        return false;
      }
      if (maxSize !== undefined && file.size > maxSize) {
        const sizeMB = maxSize / (1024 * 1024);
        toast.error(`The file must be under ${sizeMB}MB in size`);
        return false;
      }
      const fileType = deriveFileType(file);
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (
        allowedExtensions &&
        allowedExtensions.length > 0 &&
        !allowedExtensions.includes(ext)
      ) {
        toast.error(`Only following extensions are allowed: ${allowedExtensions.join(', ')}`);
        return false;
      }
      if (allowedTypes && allowedTypes.length > 0 && !allowedTypes.includes(fileType)) {
        toast.error(`Only ${allowedTypes.join(', ')} files are allowed`);
        return false;
      }
      return true;
    },
    [allowedExtensions, allowedTypes, maxSize]
  );

  /**
   * Uploads a single file using a signed URL.
   * Validates file before upload and shows toast notifications.
   *
   * @param file - File to upload
   * @returns Path/ID of uploaded file, or undefined if upload failed
   */
  const uploadFile = useCallback(
    async (file: File): Promise<string | undefined> => {
      if (!validateFile(file)) {
        return undefined;
      }

      const ext = file.name.split('.').pop() || '';
      const fileType = deriveFileType(file);

      try {
        const { fullPath, signedUrl } = await urlSigner(ext, fileType, file.name, file.size);

        const headers: Record<string, string> = {
          'Content-Type': fileType,
        };
        // Images are public, documents are not
        if (fileType.startsWith('image/')) {
          headers['x-amz-acl'] = 'public-read';
        }

        const uploadRes = await fetch(signedUrl, {
          method: 'PUT',
          body: file,
          headers,
        });

        if (uploadRes.ok) {
          if (!noSuccessToast) {
            toast.success(`File ${file.name} uploaded successfully!`);
          }
          return fullPath;
        } else {
          toast.error(`Failed to upload ${file.name}`);
          return undefined;
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error}`);
        return undefined;
      }
    },
    [noSuccessToast, urlSigner, validateFile]
  );

  /**
   * Uploads multiple files in parallel.
   * Filters out failed uploads and returns only successful paths.
   *
   * @param files - Array of files to upload
   * @returns Array of paths/IDs for successfully uploaded files
   */
  const uploadMultipleFiles = useCallback(
    async (files: File[]): Promise<string[]> => {
      const uploadPromises = files.map((file) => uploadFile(file));
      const results = await Promise.all(uploadPromises);
      const validPaths = results.filter((path): path is string => path !== undefined);
      return validPaths;
    },
    [uploadFile]
  );

  return {
    validateFile,
    uploadFile,
    uploadMultipleFiles,
  };
}
